import React, { useEffect, useState, useCallback } from "react";
import { apiClient, authFetch } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Database, Cloud, Play, Trash2, Download, RefreshCw, CheckCircle2,
  XCircle, AlertCircle, Server, HardDrive, ShieldCheck, Bell, Send,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

import { formatDateTime } from "@/lib/formatDate";

function formatDate(iso) {
  return formatDateTime(iso) || "—";
}

export default function AdminBackups() {
  const [local, setLocal] = useState({ backups: [], total_size_bytes: 0, count: 0, retention_days: 14 });
  const [cloud, setCloud] = useState({ items: [], count: 0, total_size_bytes: 0 });
  const [config, setConfig] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingLocal, setLoadingLocal] = useState(false);
  const [loadingCloud, setLoadingCloud] = useState(false);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  // Form state
  const [cfgEnabled, setCfgEnabled] = useState(false);
  const [cfgEndpoint, setCfgEndpoint] = useState("");
  const [cfgAccessKey, setCfgAccessKey] = useState("");
  const [cfgSecretKey, setCfgSecretKey] = useState("");
  const [cfgBucket, setCfgBucket] = useState("");
  const [cfgPrefix, setCfgPrefix] = useState("");
  const [cfgRegion, setCfgRegion] = useState("us-east-1");
  const [cfgRetention, setCfgRetention] = useState(30);
  // Iter64: System-Webhook
  const [whConfig, setWhConfig] = useState(null);
  const [whEnabled, setWhEnabled] = useState(false);
  const [whUrl, setWhUrl] = useState("");
  const [whSecret, setWhSecret] = useState("");
  const [whSavingCfg, setWhSavingCfg] = useState(false);
  const [whTesting, setWhTesting] = useState(false);
  // Iter65: Weekly Summary
  const [wsEnabled, setWsEnabled] = useState(true);
  const [wsLastRun, setWsLastRun] = useState(null);
  const [wsSaving, setWsSaving] = useState(false);
  const [wsRunning, setWsRunning] = useState(false);

  const loadWeeklySummary = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin/backups/weekly-summary/config");
      setWsEnabled(res.data.weekly_summary_enabled !== false);
      setWsLastRun(res.data.last_run_at);
    } catch (e) { /* silent */ }
  }, []);

  const handleSaveWeeklySummary = async (enabled) => {
    setWsSaving(true);
    try {
      await apiClient.put("/admin/backups/weekly-summary/config", {
        weekly_summary_enabled: enabled,
      });
      setWsEnabled(enabled);
      toast.success(`Weekly Summary ${enabled ? "aktiviert" : "deaktiviert"}.`);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen.");
    } finally { setWsSaving(false); }
  };

  const handleRunWeeklySummary = async () => {
    setWsRunning(true);
    try {
      const res = await apiClient.post("/admin/backups/weekly-summary/run-now");
      if (res.data?.success) {
        toast.success(`Weekly Summary gesendet (HTTP ${res.data.status_code}) ✓`);
        loadWeeklySummary();
      } else {
        toast.error(`Fehler: ${res.data?.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen.");
    } finally { setWsRunning(false); }
  };

  const loadWebhookConfig = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin/backups/webhook/config");
      setWhConfig(res.data);
      setWhEnabled(!!res.data.system_webhook_enabled);
      setWhUrl(res.data.system_webhook_url || "");
    } catch (e) { /* silent */ }
  }, []);

  const handleSaveWebhookConfig = async () => {
    setWhSavingCfg(true);
    try {
      const body = {
        system_webhook_enabled: whEnabled,
        system_webhook_url: whUrl.trim() || null,
      };
      if (whSecret) body.system_webhook_secret = whSecret;
      const res = await apiClient.put("/admin/backups/webhook/config", body);
      setWhConfig(res.data);
      setWhSecret("");
      toast.success("Webhook-Konfiguration gespeichert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setWhSavingCfg(false); }
  };

  const handleWebhookTest = async () => {
    setWhTesting(true);
    try {
      const res = await apiClient.post("/admin/backups/webhook/test");
      if (res.data?.success) {
        toast.success(`Webhook OK ✓ (HTTP ${res.data.status_code})`);
      } else {
        toast.error(`Webhook-Test fehlgeschlagen: ${res.data?.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen.");
    } finally { setWhTesting(false); }
  };

  const loadLocal = useCallback(async () => {
    setLoadingLocal(true);
    try {
      const res = await apiClient.get("/admin/backups");
      setLocal(res.data || { backups: [], total_size_bytes: 0, count: 0, retention_days: 14 });
    } catch (e) {
      toast.error("Konnte lokale Backups nicht laden.");
    } finally {
      setLoadingLocal(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin/backups/cloud/config");
      setConfig(res.data);
      setCfgEnabled(!!res.data.cloud_backup_enabled);
      setCfgEndpoint(res.data.cloud_backup_endpoint || "");
      setCfgBucket(res.data.cloud_backup_bucket || "");
      setCfgPrefix(res.data.cloud_backup_prefix || "");
      setCfgRegion(res.data.cloud_backup_region || "us-east-1");
      setCfgRetention(res.data.cloud_backup_retention_days || 30);
    } catch (e) {
      // silent
    }
  }, []);

  const loadCloud = useCallback(async () => {
    setLoadingCloud(true);
    try {
      const res = await apiClient.get("/admin/backups/cloud/list");
      setCloud(res.data || { items: [], count: 0, total_size_bytes: 0 });
    } catch (e) {
      // silent (cloud kann disabled sein)
    } finally {
      setLoadingCloud(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin/backups/jobs");
      setJobs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadLocal();
    loadConfig();
    loadCloud();
    loadJobs();
    loadWebhookConfig();
    loadWeeklySummary();
  }, [loadLocal, loadConfig, loadCloud, loadJobs, loadWebhookConfig, loadWeeklySummary]);

  const handleSaveConfig = async () => {
    setSavingCfg(true);
    try {
      const body = {
        cloud_backup_enabled: cfgEnabled,
        cloud_backup_endpoint: cfgEndpoint.trim() || null,
        cloud_backup_bucket: cfgBucket.trim() || null,
        cloud_backup_prefix: cfgPrefix.trim() || null,
        cloud_backup_region: cfgRegion.trim() || null,
        cloud_backup_retention_days: Number(cfgRetention) || 30,
      };
      if (cfgAccessKey) body.cloud_backup_access_key = cfgAccessKey;
      if (cfgSecretKey) body.cloud_backup_secret_key = cfgSecretKey;
      const res = await apiClient.put("/admin/backups/cloud/config", body);
      setConfig(res.data);
      setCfgAccessKey("");
      setCfgSecretKey("");
      toast.success("Cloud-Backup-Konfiguration gespeichert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSavingCfg(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await apiClient.post("/admin/backups/cloud/test");
      if (res.data?.success) {
        toast.success(`Verbindung zu ${res.data.bucket} OK ✓`);
      } else {
        toast.error(`Verbindung fehlgeschlagen: ${res.data?.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen.");
    } finally {
      setTesting(false);
    }
  };

  const handleRunBackup = async () => {
    if (!window.confirm("Jetzt Backup ausführen + zur Cloud hochladen?")) return;
    setRunning(true);
    try {
      const res = await apiClient.post("/admin/backups/run");
      const d = res.data;
      if (d.success) {
        const cloudMsg = d.cloud_uploaded
          ? ` · Cloud: ${d.cloud_key}`
          : d.cloud_error
            ? ` · Cloud-Fehler: ${d.cloud_error}`
            : "";
        toast.success(`Backup erstellt: ${d.file} (${formatBytes(d.size_bytes)})${cloudMsg}`);
        loadLocal();
        loadCloud();
        loadJobs();
      } else {
        toast.error(`Backup fehlgeschlagen: ${d.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup fehlgeschlagen.");
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Backup '${name}' wirklich löschen?`)) return;
    try {
      await apiClient.delete(`/admin/backups/${encodeURIComponent(name)}`);
      toast.success("Backup gelöscht.");
      loadLocal();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const [verifying, setVerifying] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const handleVerify = async (name) => {
    setVerifying(name); setVerifyResult(null);
    try {
      const r = await apiClient.post(`/admin/backups/${encodeURIComponent(name)}/verify`, null, { timeout: 300000 });
      setVerifyResult(r.data);
      r.data.ok ? toast.success(`Restore-Test bestanden (${r.data.duration_s} s): ${r.data.checks?.journal_lines_restored} Journalzeilen identisch`)
        : toast.error(`Restore-Test fehlgeschlagen: ${r.data.error || "unbekannt"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Restore-Test fehlgeschlagen"); }
    finally { setVerifying(null); }
  };

  const handleDownload = (name) => {
    const url = `${BACKEND_URL}/api/admin/backups/${encodeURIComponent(name)}/download`;
    authFetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name;
        a.click();
      })
      .catch(() => toast.error("Download fehlgeschlagen."));
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="admin-backups-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center text-white">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">DB-Backups & Cloud-Sync</h3>
            <p className="text-sm text-slate-500">
              Täglicher mongodump um 03:00 Europe/Zurich · 14 Tage lokal · 30 Tage Cloud · Infomaniak Swiss Backup
            </p>
          </div>
        </div>
        <Button
          onClick={handleRunBackup}
          disabled={running}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="admin-backups-run-now-btn"
        >
          <Play className={`w-4 h-4 mr-2 ${running ? "animate-pulse" : ""}`} />
          {running ? "Läuft…" : "Backup jetzt ausführen"}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="p-3 rounded border border-slate-200 bg-slate-50" data-testid="admin-backups-stat-local">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <HardDrive className="w-3 h-3" /> Lokal
          </div>
          <div className="text-xl font-bold text-slate-900">{local.count}</div>
          <div className="text-xs text-slate-500">{formatBytes(local.total_size_bytes)} · {local.retention_days} Tage Retention</div>
        </div>
        <div className="p-3 rounded border border-slate-200 bg-slate-50" data-testid="admin-backups-stat-cloud">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <Cloud className="w-3 h-3" /> Cloud (Infomaniak)
          </div>
          <div className="text-xl font-bold text-slate-900">{cloud.count}</div>
          <div className="text-xs text-slate-500">
            {formatBytes(cloud.total_size_bytes)} · {config?.cloud_backup_retention_days || 30} Tage Retention
          </div>
        </div>
        <div className="p-3 rounded border border-slate-200 bg-slate-50" data-testid="admin-backups-stat-status">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <ShieldCheck className="w-3 h-3" /> Status
          </div>
          <div className="text-xl font-bold">
            {config?.cloud_backup_enabled ? (
              <span className="text-emerald-700">AKTIV</span>
            ) : (
              <span className="text-slate-400">INAKTIV</span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            Cron: täglich 03:00 Europe/Zurich
          </div>
        </div>
      </div>

      {/* Cloud-Konfig */}
      <div className="border border-slate-200 rounded-lg p-4 mb-5 bg-slate-50/60" data-testid="admin-backups-cloud-config">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Cloud className="w-4 h-4 text-indigo-600" /> Cloud-Backup Konfiguration
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Aktiviert</span>
            <Switch
              checked={cfgEnabled}
              onCheckedChange={setCfgEnabled}
              data-testid="admin-backups-enabled-switch"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Endpoint</label>
            <Input value={cfgEndpoint} onChange={(e) => setCfgEndpoint(e.target.value)}
              placeholder="https://s3.swiss-backup04.infomaniak.com"
              data-testid="admin-backups-endpoint-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Bucket</label>
            <Input value={cfgBucket} onChange={(e) => setCfgBucket(e.target.value)}
              placeholder="emergent-apps-backup"
              data-testid="admin-backups-bucket-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">
              Access Key {config?.cloud_backup_access_key && <span className="text-emerald-600 text-[10px]">(Aktuell: {config.cloud_backup_access_key})</span>}
            </label>
            <Input value={cfgAccessKey} onChange={(e) => setCfgAccessKey(e.target.value)}
              placeholder={config?.has_secret ? "(leer lassen, um zu behalten)" : "Access Key"}
              data-testid="admin-backups-accesskey-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">
              Secret Key {config?.has_secret && <span className="text-emerald-600 text-[10px]">(Aktuell: {config.cloud_backup_secret_key_preview})</span>}
            </label>
            <Input type="password" value={cfgSecretKey} onChange={(e) => setCfgSecretKey(e.target.value)}
              placeholder={config?.has_secret ? "(leer lassen, um zu behalten)" : "Secret Key"}
              data-testid="admin-backups-secretkey-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Prefix (App-Trennung)</label>
            <Input value={cfgPrefix} onChange={(e) => setCfgPrefix(e.target.value)}
              placeholder="buchahltun"
              data-testid="admin-backups-prefix-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Region</label>
            <Input value={cfgRegion} onChange={(e) => setCfgRegion(e.target.value)}
              placeholder="us-east-1"
              data-testid="admin-backups-region-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Retention (Tage)</label>
            <Input type="number" value={cfgRetention} onChange={(e) => setCfgRetention(e.target.value)}
              data-testid="admin-backups-retention-input" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={handleTestConnection} disabled={testing}
            data-testid="admin-backups-test-btn">
            {testing ? "Teste…" : "Verbindung testen"}
          </Button>
          <Button onClick={handleSaveConfig} disabled={savingCfg}
            data-testid="admin-backups-save-config-btn">
            {savingCfg ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </div>

      {/* Iter64: System-Webhook für backup.completed / backup.failed */}
      <div className="border border-slate-200 rounded-lg p-4 mb-5 bg-slate-50/60" data-testid="admin-backups-webhook-config">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" /> Backup-Notifications (Slack / Discord / n8n)
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Aktiviert</span>
            <Switch
              checked={whEnabled}
              onCheckedChange={setWhEnabled}
              data-testid="admin-backups-webhook-enabled-switch"
            />
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Schickt nach jedem Backup-Lauf einen JSON-POST an die URL — mit
          <code className="mx-1 px-1 bg-white border border-slate-200 rounded">text</code> (Slack)
          und <code className="mx-1 px-1 bg-white border border-slate-200 rounded">content</code> (Discord).
          Bei gesetztem Secret wird ein <code>X-Webhook-Signature-256</code> HMAC-Header mitgeschickt.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600">Webhook-URL</label>
            <Input
              value={whUrl}
              onChange={(e) => setWhUrl(e.target.value)}
              placeholder="https://hooks.slack.com/services/...  oder  https://discord.com/api/webhooks/..."
              data-testid="admin-backups-webhook-url-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600">
              HMAC-Secret (optional)
              {whConfig?.has_secret && (
                <span className="text-emerald-600 text-[10px] ml-1">
                  Aktuell: {whConfig.system_webhook_secret_preview}
                </span>
              )}
            </label>
            <Input
              type="password"
              value={whSecret}
              onChange={(e) => setWhSecret(e.target.value)}
              placeholder={whConfig?.has_secret ? "(leer lassen, um zu behalten)" : "Beliebiger String für HMAC-SHA256 Signatur"}
              data-testid="admin-backups-webhook-secret-input"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={handleWebhookTest} disabled={whTesting || !whUrl}
            data-testid="admin-backups-webhook-test-btn">
            <Send className="w-3 h-3 mr-1" /> {whTesting ? "Sende…" : "Test-Ping senden"}
          </Button>
          <Button onClick={handleSaveWebhookConfig} disabled={whSavingCfg}
            data-testid="admin-backups-webhook-save-btn">
            {whSavingCfg ? "Speichere…" : "Speichern"}
          </Button>
        </div>

        {/* Iter65: Weekly Summary Sub-Section */}
        <div className="mt-4 pt-4 border-t border-slate-200" data-testid="admin-backups-weekly-summary">
          <div className="flex items-center justify-between mb-1">
            <h5 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              📊 Weekly Summary (Sonntag 20:00 Europe/Zurich)
            </h5>
            <Switch
              checked={wsEnabled}
              onCheckedChange={handleSaveWeeklySummary}
              disabled={wsSaving}
              data-testid="admin-backups-weekly-enabled-switch"
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Wochen-Report mit Backup-/Webhook-/Rechnungs-/Beleg-/Bank-KPIs der letzten 7 Tage.
              {wsLastRun && (
                <span className="ml-1 text-emerald-700">
                  · Zuletzt: {formatDate(wsLastRun)}
                </span>
              )}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRunWeeklySummary}
              disabled={wsRunning || !whUrl}
              data-testid="admin-backups-weekly-run-now-btn"
            >
              <Send className="w-3 h-3 mr-1" />
              {wsRunning ? "Sende…" : "Jetzt senden"}
            </Button>
          </div>
        </div>
      </div>

      {/* Lokale Backups */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-slate-600" /> Lokale Backups ({local.count})
          </h4>
          <Button size="sm" variant="outline" onClick={loadLocal} disabled={loadingLocal}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loadingLocal ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
        {local.backups.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-4 text-center border border-dashed border-slate-200 rounded"
            data-testid="admin-backups-local-empty">
            Noch keine lokalen Backups. Klicke oben auf "Backup jetzt ausführen".
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="admin-backups-local-table">
            {verifyResult && (
              <div className={`mb-2 rounded-md border px-3 py-2 text-xs ${verifyResult.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`} data-testid="admin-backups-verify-result">
                Restore-Test {verifyResult.ok ? "bestanden" : "fehlgeschlagen"} · {verifyResult.name} · {verifyResult.duration_s} s · Journalzeilen {verifyResult.checks?.journal_lines_live} / {verifyResult.checks?.journal_lines_restored} · Soll-Summe {verifyResult.checks?.journal_debit_live} / {verifyResult.checks?.journal_debit_restored}{verifyResult.error ? ` · ${verifyResult.error}` : ""}
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 border-b">
                  <th className="py-2 px-2">Datei</th>
                  <th className="py-2 px-2">Größe</th>
                  <th className="py-2 px-2">Erstellt</th>
                  <th className="py-2 px-2 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {local.backups.map((b) => (
                  <tr key={b.name} className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`admin-backups-local-row-${b.name}`}>
                    <td className="py-2 px-2 font-mono text-xs">{b.name}</td>
                    <td className="py-2 px-2">{formatBytes(b.size_bytes)}</td>
                    <td className="py-2 px-2 text-slate-600 text-xs">{formatDate(b.created_at)}</td>
                    <td className="py-2 px-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => handleVerify(b.name)} disabled={!!verifying}
                        title="Restore-Test: Backup in Prüf-Datenbank zurückspielen und Kennzahlen vergleichen"
                        data-testid={`admin-backups-verify-${b.name}`}>
                        <ShieldCheck className={`w-3.5 h-3.5 ${verifying === b.name ? "animate-pulse" : ""}`} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDownload(b.name)}
                        title="Download"
                        data-testid={`admin-backups-download-${b.name}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(b.name)}
                        className="text-rose-600 hover:bg-rose-50"
                        title="Löschen"
                        data-testid={`admin-backups-delete-${b.name}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cloud Backups */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Cloud className="w-4 h-4 text-blue-600" /> Cloud-Backups ({cloud.count})
            {config?.cloud_backup_bucket && (
              <Badge variant="outline" className="text-[10px] font-mono">
                {config.cloud_backup_bucket}/{config.cloud_backup_prefix}/
              </Badge>
            )}
          </h4>
          <Button size="sm" variant="outline" onClick={loadCloud} disabled={loadingCloud}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loadingCloud ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
        {cloud.items.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-4 text-center border border-dashed border-slate-200 rounded"
            data-testid="admin-backups-cloud-empty">
            {config?.cloud_backup_enabled
              ? "Keine Cloud-Backups gefunden. Triggere einen Backup oder warte auf den 03:00 Cron."
              : "Cloud-Backup deaktiviert. Aktiviere oben + speichere die Credentials."}
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="admin-backups-cloud-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 border-b">
                  <th className="py-2 px-2">Object</th>
                  <th className="py-2 px-2">Größe</th>
                  <th className="py-2 px-2">Last Modified</th>
                </tr>
              </thead>
              <tbody>
                {cloud.items.map((b) => (
                  <tr key={b.key} className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`admin-backups-cloud-row-${b.name}`}>
                    <td className="py-2 px-2 font-mono text-xs break-all">{b.key}</td>
                    <td className="py-2 px-2">{formatBytes(b.size_bytes)}</td>
                    <td className="py-2 px-2 text-slate-600 text-xs">{formatDate(b.last_modified)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Job-Log */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Server className="w-4 h-4 text-slate-600" /> Job-Historie
          </h4>
          <Button size="sm" variant="outline" onClick={loadJobs}>
            <RefreshCw className="w-3 h-3 mr-1" /> Aktualisieren
          </Button>
        </div>
        {jobs.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-3 text-center" data-testid="admin-backups-jobs-empty">
            Noch keine Backup-Jobs.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="admin-backups-jobs-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-slate-500 border-b">
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Zeit</th>
                  <th className="py-2 px-2">Datei</th>
                  <th className="py-2 px-2">Größe</th>
                  <th className="py-2 px-2">Dauer</th>
                  <th className="py-2 px-2">Cloud</th>
                  <th className="py-2 px-2">Quelle</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-slate-100">
                    <td className="py-2 px-2">
                      {j.success ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-rose-600" />
                      )}
                    </td>
                    <td className="py-2 px-2 font-mono text-xs">{formatDate(j.run_at)}</td>
                    <td className="py-2 px-2 font-mono text-xs truncate max-w-[200px]" title={j.file}>{j.file || "—"}</td>
                    <td className="py-2 px-2">{formatBytes(j.size_bytes)}</td>
                    <td className="py-2 px-2 text-xs">{j.duration_ms} ms</td>
                    <td className="py-2 px-2">
                      {j.cloud_uploaded ? (
                        <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">UP</Badge>
                      ) : j.cloud_error ? (
                        <Badge className="bg-rose-100 text-rose-800 text-[10px]" title={j.cloud_error}>FAIL</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-[10px]">—</Badge>
                      )}
                    </td>
                    <td className="py-2 px-2 text-xs">{j.triggered_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!config?.cloud_backup_enabled && (
        <div className="mt-4 p-3 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800 flex items-start gap-2"
          data-testid="admin-backups-warning-disabled">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Cloud-Backup ist deaktiviert.</strong> Backups werden nur lokal in <code>/app/backups</code> gespeichert
            und gehen bei Pod-Recreation verloren. Aktiviere oben + speichere Infomaniak-Credentials.
          </div>
        </div>
      )}
    </Card>
  );
}
